import { useEffect, useRef, useState } from "react";
// auth import removed — MFA gate temporarily relaxed (see line ~766)
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Terminal,
  Shield,
  LogOut,
  Home,
  ClipboardList,
  GraduationCap,
  FileText,
  ClipboardCheck,
  UserCheck,
  Users,
  LibraryBig,
  Cable,
  Activity,
  Briefcase,
  MoreHorizontal,
  MapPin,
  Building2,
  FileSearch,
  CreditCard,
  BookText,
  BadgeCheck,
  MessagesSquare,
  ClipboardPlus,
  Bug,
  Smartphone,
  User as UserIcon,
} from "lucide-react";
import logoSvg from "../../assets/logo.svg";
import { useAuth } from "../../context/AuthContext";
import type { SubscriptionTier } from "../../context/AuthContext";
import { copySupportBundleToClipboard } from "../../lib/support";
import {
  canManageBillingWorkspace,
  canManageFacilityAccess,
  canManageOrganizationAccess,
} from "../../lib/services/adminUsers";
import { canManageIntegrationWorkspace } from "../../lib/services/integrations";
import { useHasCapability } from "../../hooks/useCapability";
import { useQuery } from "@tanstack/react-query";
import {
  getFacilityFeatureFlags,
  getFacilities,
  type FacilityFeatureFlag,
  type FacilityOption,
} from "../../lib/services/facilities";
import { canAccessQapiLeadershipWorkspace } from "../../lib/services/qapiLeadership";
import { useTier, useIsMultiFacility } from "../../hooks/useTier";
import { useBugButtonVisibility } from "../../hooks/useBugButtonVisibility";
import { NotificationBell } from "./NotificationBell";
import { appHostExternalRouteUrl, getFieldAppUrl, isFieldAppAllowedPath, isFieldAppHost, isFieldAppRoutePath } from "../../lib/fieldAppHost";
import { SmeFeedbackOverlay } from "../feedback/SmeFeedbackOverlay";

const BREADCRUMB_LABELS: Record<string, string> = {
  visits: "Visits",
  review: "Review",
  standard: "Standard",
  trends: "Trends",
  training: "Training",
  overview: "Overview",
  library: "Library",
  tracks: "Tracks",
  assignments: "Assignments",
  learner: "Learner",
  transcript: "Evidence",
  reports: "Reports",
  actions: "Actions",
  reviewer: "Reviewer",
  admin: "Admin",
  users: "Users",
  "facility-access": "Facility Access",
  superadmin: "Superadmin",
  "organization-access": "Organization Access",
  "sme-feedback": "SME Feedback",
  releases: "Releases",
  integrations: "Integrations",
  status: "Status",
  runs: "Runs",
  "self-monitoring": "Self Monitoring",
  checklist: "Checklist",
  entry: "Entry",
  assessments: "Acquisitions",
  acquisition: "Acquisition",
  "service-line": "Service Line",
  "chart-audit": "Chart Audit",
  qapi: "QAPI",
  insurance: "Insurance",
  credentialing: "Credentialing",
  "patient-comms": "Patient Comms",
  pat: "PAT Intake",
  billing: "Billing",
  onboarding: "Onboarding",
  policies: "Policies",
};

function isDynamicPathToken(part: string): boolean {
  return /^[0-9a-f-]{8,}$/i.test(part);
}

export function buildBreadcrumbs(
  pathname: string,
): Array<{ label: string; to: string }> {
  const parts = pathname.split("/").filter(Boolean);
  const items: Array<{ label: string; to: string }> = [
    { label: "Home", to: "/" },
  ];
  let currentPath = "";

  for (const part of parts) {
    currentPath += `/${part}`;
    if (isDynamicPathToken(part)) continue;

    const label =
      BREADCRUMB_LABELS[part] ||
      part.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    if (items[items.length - 1]?.label === label) continue;

    items.push({ label, to: currentPath });
  }

  return items;
}

export function isFieldAppRoute(pathname: string, fieldAppHost = isFieldAppHost()): boolean {
  return isFieldAppRoutePath(pathname) || (fieldAppHost && isFieldAppAllowedPath(pathname));
}

/**
 * Tier-aware primary nav items.
 * Standalone/Facility: Overview | Actions | Training  (trends + QAPI on home page)
 * Consultant/Regional: Overview | Clients | Survey | Reports | Actions
 * Enterprise Corporate: Portfolio | Facilities | Actions | Training
 * FieldSurveyor: Facilities | Surveys | Profile  (all other nav absent)

 */
function getPrimaryNavItems(
  tier: SubscriptionTier,
  isMultiFacility: boolean,
  role: string,
) {
  // Stripped nav for field surveyors — only facilities, surveys, profile
  if (role === 'FieldSurveyor') {
    return [
      {
        to: '/today',
        label: 'Today',
        icon: Home,
        match: (p: string) => p === '/today',
      },
      {
        to: '/facilities',
        label: 'Facilities',
        icon: Building2,
        match: (p: string) => p.startsWith('/facilities'),
      },
      {
        to: '/profile',
        label: 'Profile',
        icon: UserIcon,
        match: (p: string) => p.startsWith('/profile'),
      },
    ];
  }
  // Enterprise facility-level users see Standalone nav
  if (tier === "enterprise" && !isMultiFacility) {
    return [
      {
        to: "/",
        label: "Overview",
        icon: Home,
        match: (p: string) => p === "/",
      },
      {
        to: "/actions",
        label: "Actions",
        icon: ClipboardList,
        match: (p: string) => p.startsWith("/actions"),
      },
      {
        to: "/training",
        label: "Training",
        icon: GraduationCap,
        match: (p: string) => p.startsWith("/training"),
      },
    ];
  }

  if (
    tier === "consultant" ||
    (tier === "enterprise" && isMultiFacility && role !== "Admin")
  ) {
    return [
      {
        to: "/",
        label: "Overview",
        icon: Home,
        match: (p: string) => p === "/",
      },
      {
        to: "/visits",
        label: "Clients",
        icon: MapPin,
        match: (p: string) => p.startsWith("/visits"),
      },
      {
        to: "/visits",
        label: "Survey",
        icon: ClipboardCheck,
        match: () => false,
      },
      {
        to: "/reports",
        label: "Reports",
        icon: FileText,
        match: (p: string) => p.startsWith("/reports"),
      },
      {
        to: "/actions",
        label: "Actions",
        icon: ClipboardList,
        match: (p: string) => p.startsWith("/actions"),
      },
    ];
  }

  if (tier === "enterprise" && isMultiFacility) {
    return [
      {
        to: "/",
        label: "Portfolio",
        icon: Building2,
        match: (p: string) => p === "/",
      },
      {
        to: "/visits",
        label: "Facilities",
        icon: MapPin,
        match: (p: string) => p.startsWith("/visits"),
      },
      {
        to: "/actions",
        label: "Actions",
        icon: ClipboardList,
        match: (p: string) => p.startsWith("/actions"),
      },
      {
        to: "/training",
        label: "Training",
        icon: GraduationCap,
        match: (p: string) => p.startsWith("/training"),
      },
    ];
  }

  // Standalone (default) — trends + QAPI are on the home page
  return [
    { to: "/", label: "Overview", icon: Home, match: (p: string) => p === "/" },
    {
      to: "/actions",
      label: "Actions",
      icon: ClipboardList,
      match: (p: string) => p.startsWith("/actions"),
    },
    {
      to: "/training",
      label: "Training",
      icon: GraduationCap,
      match: (p: string) => p.startsWith("/training"),
    },
  ];
}

const TIER_BADGE_STYLES: Record<SubscriptionTier, string> = {
  standalone: "bg-teal-100 text-teal-700",
  consultant: "bg-amber-100 text-amber-700",
  enterprise: "bg-blue-100 text-blue-700",
  field_surveyor_solo: "bg-violet-100 text-violet-700",
};

const TIER_LABELS: Record<SubscriptionTier, string> = {
  standalone: "ASC",
  consultant: "Consultant",
  enterprise: "Enterprise",
  field_surveyor_solo: "Surveyor",
};

/**
 * NavBar — Tier-aware grouped navigation with "More" dropdown.
 * Primary items vary by tier+role; secondary items behind a "More" toggle.
 */
function NavBar({
  navClass,
  location,
  tier,
  isMultiFacility,
  role,
  isSuperadmin,
  showFacilityAccessNav,
  showOrganizationAccessNav,
  showBillingNav,
  showIntegrationsNav,
  showQapiLeadershipNav,
  enabledModules,
  canRunAssessment,
  onCopySupport,
  onSignOut,
}: {
  navClass: (isActive: boolean) => string;
  location: { pathname: string };
  tier: SubscriptionTier;
  isMultiFacility: boolean;
  role: string;
  isSuperadmin: boolean;
  enabledModules: Set<string>;
  canRunAssessment: boolean;
  showFacilityAccessNav: boolean;
  showOrganizationAccessNav: boolean;
  showBillingNav: boolean;
  showIntegrationsNav: boolean;
  showQapiLeadershipNav: boolean;
  onCopySupport: () => Promise<void>;
  onSignOut: () => void;
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [supportStatus, setSupportStatus] = useState<string | null>(null);
  const [isCopyingSupport, setIsCopyingSupport] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const [bugButtonEnabled, , toggleBugButton] = useBugButtonVisibility();

  useEffect(() => {
    if (!moreOpen) return;
    const handler = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node))
        setMoreOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [moreOpen]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setMoreOpen(false);
      setSupportStatus(null);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  const primaryItems = getPrimaryNavItems(tier, isMultiFacility, role);
  const primaryPaths = new Set(primaryItems.map((i) => i.to));
  const showFacilityOperationalNav = !isMultiFacility && canRunAssessment;

  const isMoreActive = [
    "/actions",
    "/self-monitoring",
    "/assessments",
    "/chart-audit",
    "/qapi",
    "/insurance",
    "/billing",
    "/reviewer",
    "/admin",
    "/superadmin",
    "/integrations",
    "/reports",
    "/training",
  ]
    .filter((p) => !primaryPaths.has(p))
    .some((p) => location.pathname.startsWith(p));

  // FieldSurveyor pages all render FieldAppShell which has its own bottom
  // nav (Today / Facilities / Profile). Suppress AppShell's nav to avoid
  // rendering two overlapping bottom bars.
  if (role === 'FieldSurveyor') {
    return null
  }

  return (
    <nav className="v11-nav-shell">
      <div className="mx-auto flex max-w-7xl items-center justify-around gap-1 md:justify-start md:gap-2">
        {primaryItems.map((item) => (
          <Link
            key={item.label}
            to={item.to}
            className={navClass(item.match(location.pathname))}
          >
            <item.icon size={18} />
            <span>{item.label}</span>
          </Link>
        ))}

        {/*
         * Field App entry promoted to primary nav for anyone with the
         * `run_assessment` capability — surveyors, facility admins, and
         * DON-delegated staff (staff users an admin has granted
         * `run_assessment` to via granted_capabilities). Keeps the dropdown
         * entry too as a keyboard-accessible fallback. Uses getFieldAppUrl
         * so we route to app.ascsurvey.com / app.staging.ascsurvey.com on
         * prod/staging, and stay on the local origin on dev tunnels.
         */}
        {canRunAssessment && (
          <a
            href={getFieldAppUrl("/self-monitoring")}
            className={navClass(location.pathname.startsWith("/self-monitoring"))}
          >
            <Smartphone size={18} />
            <span>Field App</span>
          </a>
        )}

        {/* More dropdown */}
        <div className="relative" ref={moreRef}>
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            className={navClass(isMoreActive)}
          >
            <MoreHorizontal size={18} />
            <span>More</span>
          </button>
          {moreOpen && (
            <div className="v11-nav-menu">
              {/* Show items not in primary nav */}
              {!primaryPaths.has("/reports") && (
                <Link
                  to="/reports"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <FileText size={16} className="text-slate-400" /> Reports
                </Link>
              )}
              {!primaryPaths.has("/training") && (
                <Link
                  to="/training"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <GraduationCap size={16} className="text-slate-400" />{" "}
                  Training
                </Link>
              )}
              {!primaryPaths.has("/actions") && (
                <Link
                  to="/actions"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <ClipboardCheck size={16} className="text-slate-400" />{" "}
                  Actions
                </Link>
              )}
              {enabledModules.has("qapi") &&
                tier !== "standalone" &&
                canRunAssessment && (
                  <Link
                    to="/self-monitoring"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    onClick={() => setMoreOpen(false)}
                  >
                    <Activity size={16} className="text-slate-400" /> Self
                    Monitoring
                  </Link>
                )}
              {canRunAssessment && (
                <a
                  href={getFieldAppUrl("/self-monitoring")}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <Smartphone size={16} className="text-slate-400" /> Daily Survey App
                </a>
              )}
              {showQapiLeadershipNav && (
                <Link
                  to="/qapi"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <Activity size={16} className="text-slate-400" /> QAPI
                </Link>
              )}
              {enabledModules.has("acquisition") && tier === "enterprise" && canRunAssessment && (
                <Link
                  to="/assessments/acquisition"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <Briefcase size={16} className="text-slate-400" /> Acquisitions
                </Link>
              )}
              {showFacilityOperationalNav && (
                <Link
                  to="/chart-audit"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <FileSearch size={16} className="text-slate-400" /> Chart Audit
                </Link>
              )}
              {showFacilityOperationalNav && enabledModules.has("insurance_verification") && (
                <Link
                  to="/insurance"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <Shield size={16} className="text-slate-400" /> Insurance
                </Link>
              )}
              {enabledModules.has("policy_management") && (
                <Link
                  to="/policies"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <BookText size={16} className="text-slate-400" /> Policies
                </Link>
              )}
              {showFacilityOperationalNav && enabledModules.has("credentialing") && (
                <Link
                  to="/credentialing"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <BadgeCheck size={16} className="text-slate-400" />{" "}
                  Credentialing
                </Link>
              )}
              {showFacilityOperationalNav && enabledModules.has("patient_comms") && (
                <Link
                  to="/patient-comms"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <MessagesSquare size={16} className="text-slate-400" /> Patient Comms
                </Link>
              )}
              {showFacilityOperationalNav && enabledModules.has("pat_automation") && (
                <Link
                  to="/pat"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <ClipboardPlus size={16} className="text-slate-400" /> PAT Intake
                </Link>
              )}
              {(showBillingNav || showIntegrationsNav || showFacilityAccessNav || showOrganizationAccessNav) && (
                <p className="px-4 pb-1 pt-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
                  Account Profile
                </p>
              )}
              {showBillingNav && (
                <Link
                  to="/billing"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <CreditCard size={16} className="text-slate-400" /> Billing
                </Link>
              )}

              <div className="mx-3 my-2 border-t border-slate-100" />

              {(role === "Reviewer" || role === "Admin") && (
                <Link
                  to="/reviewer"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <UserCheck size={16} className="text-slate-400" /> Reviewer
                </Link>
              )}
              {showFacilityAccessNav && (
                <Link
                  to="/admin/facility-access"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <Users size={16} className="text-slate-400" /> Facility Access
                </Link>
              )}
              {isSuperadmin && (
                <>
                  {showOrganizationAccessNav && (
                    <Link
                      to="/superadmin/organization-access"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      onClick={() => setMoreOpen(false)}
                    >
                      <LibraryBig size={16} className="text-slate-400" /> Org
                      Access
                    </Link>
                  )}
                  <Link
                    to="/admin/users"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    onClick={() => setMoreOpen(false)}
                  >
                    <Users size={16} className="text-slate-400" /> Admin
                  </Link>
                  <Link
                    to="/superadmin/releases"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    onClick={() => setMoreOpen(false)}
                  >
                    <LibraryBig size={16} className="text-slate-400" /> Releases
                  </Link>
                  <Link
                    to="/superadmin/sme-feedback"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    onClick={() => setMoreOpen(false)}
                  >
                    <Bug size={16} className="text-slate-400" /> SME Feedback
                  </Link>
                </>
              )}
              {showIntegrationsNav && (
                <Link
                  to="/integrations/status"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMoreOpen(false)}
                >
                  <Cable size={16} className="text-slate-400" /> Integrations
                </Link>
              )}

              <div className="mx-3 my-2 border-t border-slate-100" />

              <button
                onClick={async () => {
                  setIsCopyingSupport(true);
                  setSupportStatus(null);
                  try {
                    await onCopySupport();
                    setSupportStatus("Support info copied. No PHI included.");
                  } catch {
                    setSupportStatus("Support info could not be copied. Try again or contact support.");
                  } finally {
                    setIsCopyingSupport(false);
                  }
                }}
                disabled={isCopyingSupport}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Terminal size={16} className="text-slate-400" /> {isCopyingSupport ? "Copying support info..." : "Support"}
              </button>
              {supportStatus && (
                <p className="px-4 pb-2 text-xs font-semibold text-slate-500" role="status">
                  {supportStatus}
                </p>
              )}
              <button
                onClick={() => {
                  onSignOut();
                  setMoreOpen(false);
                }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 md:hidden"
              >
                <LogOut size={16} /> Sign Out
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={toggleBugButton}
          aria-pressed={bugButtonEnabled}
          aria-label={bugButtonEnabled ? "Hide bug report button" : "Show bug report button"}
          title={bugButtonEnabled ? "Hide bug report button" : "Show bug report button"}
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-600 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          data-testid="bug-button-toggle"
        >
          <Bug size={14} />
          <span>Bug: {bugButtonEnabled ? "ON" : "OFF"}</span>
        </button>
      </div>
    </nav>
  );
}

/**
 * App Shell [FE.0.3]
 * Main layout with navigation and tenant/role awareness.
 */
export function AppShell() {
  const { viewerContext, signOut } = useAuth();
  const tier = useTier();
  const isMultiFacility = useIsMultiFacility();
  const [bugButtonEnabled, , toggleBugButton] = useBugButtonVisibility();
  const showFacilityAccessNav = canManageFacilityAccess(viewerContext);
  const showOrganizationAccessNav = canManageOrganizationAccess(viewerContext);
  const showBillingNav = canManageBillingWorkspace(viewerContext);
  const showIntegrationsNav = canManageIntegrationWorkspace(viewerContext);
  const canRunAssessment = useHasCapability("run_assessment");
  const navigate = useNavigate();
  const location = useLocation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Load facility feature flags to control module-dependent nav items
  const featureFlagsQuery = useQuery<FacilityFeatureFlag[]>({
    queryKey: ["nav-feature-flags"],
    queryFn: () => getFacilityFeatureFlags(),
    staleTime: 5 * 60 * 1000,
  });
  const enabledModules = new Set(
    (featureFlagsQuery.data || [])
      .filter((f) => f.enabled)
      .map((f) => f.feature_key),
  );
  const showQapiLeadershipNav =
    enabledModules.has("qapi") && canAccessQapiLeadershipWorkspace(viewerContext);

  // Facility selector for consultant/regional tiers
  const facilitiesQuery = useQuery<FacilityOption[]>({
    queryKey: ["nav-facilities"],
    queryFn: () => getFacilities(),
    staleTime: 5 * 60 * 1000,
    enabled: isMultiFacility,
  });
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>("");

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const handleCopySupport = async () => {
    await copySupportBundleToClipboard();
  };

  const navClass = (isActive: boolean) =>
    `shrink-0 rounded-2xl px-3 py-2.5 md:px-4 md:py-3 flex flex-col items-center gap-1 text-[10px] md:flex-row md:gap-2 md:text-sm font-black transition-all ${
      isActive
        ? "bg-[var(--brand-accent)] text-[#162033] shadow-sm"
        : "text-slate-500 hover:bg-white/70 hover:text-[var(--brand-ink)]"
    }`;
  const breadcrumbs = buildBreadcrumbs(location.pathname);
  const appHostRedirect = appHostExternalRouteUrl(
    location.pathname,
    location.search,
    location.hash,
    viewerContext?.role ?? null,
  );
  const externalAppHostRedirect = appHostRedirect && !appHostRedirect.startsWith("/") ? appHostRedirect : null;

  useEffect(() => {
    if (externalAppHostRedirect) {
      window.location.replace(externalAppHostRedirect);
    }
  }, [externalAppHostRedirect]);

  // Must be declared before any conditional returns to satisfy Rules of Hooks
  useEffect(() => {
    if (viewerContext?.role !== 'FieldSurveyor') return
    if (location.pathname === '/profile/mfa') return
    // MFA gate temporarily relaxed during design/debug sessions.
    // Re-enable once MFASetupPage renders correctly on tunnel domains.
    // auth.mfa.listFactors().then(({ data }) => {
    //   const has = (data?.totp ?? []).some((f: { status: string }) => f.status === 'verified')
    //   if (!has) navigate('/profile/mfa', { replace: true })
    // })
  }, [viewerContext?.role, location.pathname, navigate])

  if (appHostRedirect) {
    if (appHostRedirect.startsWith("/")) {
      return <Navigate to={appHostRedirect} replace />;
    }

    return (
      <div className="min-h-screen bg-[#f4f8fb] p-8 text-sm font-semibold text-slate-600">
        Opening normal app...
      </div>
    );
  }

  // field_surveyors are blocked from platform routes — redirect to today.
  const FIELD_SURVEYOR_BLOCKED = [
    '/dashboard', '/trends', '/training', '/actions', '/admin', '/superadmin',
    '/integrations', '/billing', '/chart-audit', '/assessments', '/qapi',
    '/insurance', '/credentialing', '/patient-comms', '/pat', '/policies',
    '/reviewer', '/self-monitoring',
  ];
  if (
    viewerContext?.role === 'FieldSurveyor' &&
    FIELD_SURVEYOR_BLOCKED.some((p) => location.pathname.startsWith(p))
  ) {
    return <Navigate to="/today" replace />;
  }

  if (isFieldAppRoute(location.pathname)) {
    return (
      <div className="min-h-screen bg-[#f4f8fb]">
        {!isOnline && (
          <div className="glass-banner sticky top-0 z-50 flex items-center justify-center gap-2">
            <Shield size={16} />
            <span>Offline mode active, pending sync</span>
          </div>
        )}
        <div className="sticky top-0 z-40 flex justify-end border-b border-slate-200 bg-white/80 px-3 py-2 backdrop-blur">
          <button
            type="button"
            onClick={toggleBugButton}
            aria-pressed={bugButtonEnabled}
            aria-label={bugButtonEnabled ? "Hide bug report button" : "Show bug report button"}
            title={bugButtonEnabled ? "Hide bug report button" : "Show bug report button"}
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-600 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            data-testid="bug-button-toggle"
          >
            <Bug size={14} />
            <span>Bug: {bugButtonEnabled ? "ON" : "OFF"}</span>
          </button>
        </div>
        <main className="min-h-screen">
          <Outlet />
        </main>
        <SmeFeedbackOverlay />
      </div>
    );
  }

  return (
    <div className="v11-app-background flex min-h-screen flex-col">
      {!isOnline && (
        <div className="glass-banner sticky top-0 z-50 flex items-center justify-center gap-2">
          <Shield size={16} />
          <span>Offline mode active, pending sync</span>
        </div>
      )}

      <header className="v11-topbar">
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="rounded-2xl bg-white/92 px-3 py-2 shadow-sm">
              <img src={logoSvg} alt="ASC Survey" className="h-8" />
            </Link>
            {isMultiFacility &&
              facilitiesQuery.data &&
              facilitiesQuery.data.length > 1 && (
                <select
                  value={selectedFacilityId}
                  onChange={(e) => setSelectedFacilityId(e.target.value)}
                  className="v11-facility-select"
                >
                  <option value="">
                    All Facilities ({facilitiesQuery.data.length})
                  </option>
                  {facilitiesQuery.data.map((f) => (
                    <option key={f.facility_id} value={f.facility_id}>
                      {f.facility_name}
                    </option>
                  ))}
                </select>
              )}
          </div>
          <div className="hidden items-center gap-2 md:flex">
            <span className={`v11-chip ${TIER_BADGE_STYLES[tier]}`}>
              {TIER_LABELS[tier]}
            </span>
            <span className="v11-chip">
              {viewerContext?.role || "Surveyor"}
            </span>
            {enabledModules.has("notifications") && <NotificationBell />}
            <button
              aria-label="Sign Out"
              onClick={handleSignOut}
              className="rounded-2xl p-2 text-white/65 transition-colors hover:bg-white/10 hover:text-red-100"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <section className="v11-breadcrumb-bar">
        <div className="mx-auto flex w-full max-w-7xl items-center gap-2 px-4 py-2 text-xs text-slate-500">
          {breadcrumbs.map((crumb, index) => (
            <div key={crumb.to} className="flex items-center gap-2">
              {index > 0 && <span>/</span>}
              {index === breadcrumbs.length - 1 ? (
                <span className="font-semibold text-slate-700">
                  {crumb.label}
                </span>
              ) : (
                <Link to={crumb.to} className="hover:text-slate-700">
                  {crumb.label}
                </Link>
              )}
            </div>
          ))}
        </div>
      </section>

      <main className="mx-auto w-full max-w-7xl flex-grow px-4 py-6 pb-24 md:py-8 md:pb-24">
        <Outlet />
      </main>
      <SmeFeedbackOverlay />

      <NavBar
        navClass={navClass}
        location={location}
        tier={tier}
        isMultiFacility={isMultiFacility}
        role={viewerContext?.role || "Surveyor"}
        isSuperadmin={viewerContext?.is_superadmin === true}
        showFacilityAccessNav={showFacilityAccessNav}
        showOrganizationAccessNav={showOrganizationAccessNav}
        showBillingNav={showBillingNav}
        showIntegrationsNav={showIntegrationsNav}
        showQapiLeadershipNav={showQapiLeadershipNav}
        enabledModules={enabledModules}
        canRunAssessment={canRunAssessment}
        onCopySupport={handleCopySupport}
        onSignOut={handleSignOut}
      />

      <footer className="mb-20 border-t border-[rgba(10,36,64,0.12)] py-6 text-center">
        <p className="text-sm font-semibold tracking-wide text-[var(--brand-primary)]">
          Compliance. Accuracy. Security.
        </p>
      </footer>
    </div>
  );
}
